import { memo, useMemo } from "react";

import { StatusReservation } from "@/lib/gql/__generated__/graphql";

import { EditGuideTooltip } from "@/components/features/items/edit/_shared/ui/components/system/tooltip/edit-guide-tooltip";

import { Label } from "@/components/ui/label/label";

type Props = {
	statusReservation: StatusReservation;
};

export const ReservationStatusOfManagementStatusCompositions = memo(({ statusReservation }: Props) => {
	/**
	 * 事前決済（国内発行カード）かどうか
	 */
	const isReserveCreditPayment = useMemo(() => {
		return statusReservation === StatusReservation.ReserveCreditPayment;
	}, [ statusReservation ]);
	/**
	 * 事前決済（海外発行カード）かどうか
	 */
	const isReserveCreditPaymentOverseas = useMemo(() => {
		return statusReservation === StatusReservation.ReserveCreditPaymentOverseas;
	}, [ statusReservation ]);
	/**
	 * 現地決済かどうか
	 */
	const isReserveLocalPayment = useMemo(() => {
		return statusReservation === StatusReservation.ReserveLocalPayment;
	}, [ statusReservation ]);
	/**
	 * 無料決済かどうか
	 */
	const isReserveFree = useMemo(() => {
		return statusReservation === StatusReservation.ReserveFree;
	}, [ statusReservation ]);
	/**
	 * テスト予約かどうか
	 */
	const isReserveTest = useMemo(() => {
		return statusReservation === StatusReservation.ReserveTest;
	}, [ statusReservation ]);
	/**
	 * 決済エラーにより現地徴収かどうか
	 */
	const isReserveOnsitePaymentForPaymentError = useMemo(() => {
		return statusReservation === StatusReservation.ReserveOnsitePaymentForPaymentError;
	}, [ statusReservation ]);
	/**
	 * 決済エラーによる自動キャンセルかどうか
	 */
	const isCancelAutoPaymentError = useMemo(() => {
		return statusReservation === StatusReservation.CancelAutoPaymentError;
	}, [ statusReservation ]);
	/**
	 * 無料キャンセルかどうか
	 */
	const isCancelFree = useMemo(() => {
		return statusReservation === StatusReservation.CancelFree;
	}, [ statusReservation ]);
	/**
	 * キャンセル料金徴収済かどうか
	 */
	const isCancelFeeCollected = useMemo(() => {
		return statusReservation === StatusReservation.CancelFeeCollected;
	}, [ statusReservation ]);
	/**
	 * キャンセル料金未徴収かどうか
	 */
	const isCancelFeeNotCollected = useMemo(() => {
		return statusReservation === StatusReservation.CancelFeeNotCollected;
	}, [ statusReservation ]);

	return (
		<div className="w-full max-w-xl space-y-2">
			<div className="flex items-center gap-2">
				<p>予約ステータス</p>

				<EditGuideTooltip
					guide={`
            <b>決済エラーによる自動キャンセル</b>
            <br />
            事前決済予約について与信枠確保処理が失敗し、システムにより自動でキャンセルされた予約です。
            <br /><br />
            <b>決済エラーにより現地徴収</b>
            <br />
            チェックイン当日の決済キャプチャ失敗、または海外発行カード(※2026年9月以降)での与信枠確保処理が失敗したことにより、現地もしくは振込での徴収が必要となった予約です。
          `}
				/>
			</div>

			{/* 1行目：事前決済 */}
			<div className="flex flex-wrap gap-2">
				<Label text="宿泊(事前決済・国内発行カード)" variant={isReserveCreditPayment ? "link" : "black"} />
				<Label text="宿泊(事前決済・海外発行カード)" variant={isReserveCreditPaymentOverseas ? "link" : "black"} />
			</div>

			{/* 2行目：現地・無料・テスト */}
			<div className="flex flex-wrap gap-2">
				<Label text="宿泊(現地決済)" variant={isReserveLocalPayment ? "link" : "black"} />
				<Label text="宿泊(無料)" variant={isReserveFree ? "link" : "black"} />
				<Label text="宿泊(テスト予約)" variant={isReserveTest ? "link" : "black"} />
			</div>

			{/* 3行目：決済エラー系 */}
			<div className="flex flex-wrap gap-2">
				<Label text="決済エラーによる自動キャンセル" variant={isCancelAutoPaymentError ? "required" : "black"} />
				<Label text="決済エラーにより現地徴収" variant={isReserveOnsitePaymentForPaymentError ? "required" : "black"} />
			</div>

			{/* 4行目：キャンセル系（無料・キャンセル料金） */}
			<div className="flex flex-wrap gap-2">
				<Label text="無料キャンセル" variant={isCancelFree ? "required" : "black"} />
				<Label text="キャンセル料金徴収済" variant={isCancelFeeCollected ? "required" : "black"} />
				<Label text="キャンセル料金未徴収" variant={isCancelFeeNotCollected ? "required" : "black"} />
			</div>
		</div>
	);
});
